import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  Badge,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Dropdown,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  Option,
  Spinner,
  Tab,
  TabList,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Text,
  Textarea,
  tokens,
} from '@fluentui/react-components';
import {
  ArrowSyncCircleRegular,
  CheckmarkCircleRegular,
  ChevronDownRegular,
  ChevronUpRegular,
  DeleteRegular,
  DismissRegular,
  ErrorCircleRegular,
  WarningRegular,
} from '@fluentui/react-icons';
import { useApi } from '../api/useApi';
import { useAsync } from '../api/useAsync';
import { PageHeader } from '../components/PageHeader';
import type {
  PimAssignmentDto,
  PimAssignmentKind,
  PimFrequency,
  PimGovernanceSettingsDto,
  PimMemberType,
  PimOverviewDto,
  PimRoleOverviewDto,
  PimSystem,
  PimSystemFilter,
} from '../api/types';
import './pim.css';

const SYSTEMS: { value: PimSystemFilter; label: string }[] = [
  { value: 'Entra', label: 'Entra ID' },
  { value: 'Azure', label: 'ARM' },
  { value: 'Both', label: 'Entra + ARM' },
];

type PimTab = 'roles' | 'stale';

const UNUSED_OPTIONS: { value: number; label: string }[] = [
  { value: 14, label: '14 Tage' },
  { value: 30, label: '1 Monat' },
  { value: 90, label: '3 Monate' },
  { value: 180, label: '6 Monate' },
];

const FREQ: Record<PimFrequency, { label: string; icon: ReactNode; cls: string }> = {
  Frequent: { label: 'häufig', icon: <CheckmarkCircleRegular />, cls: 'pim-freq-frequent' },
  Regular: { label: 'regelmäßig', icon: <ArrowSyncCircleRegular />, cls: 'pim-freq-regular' },
  Rare: { label: 'selten', icon: <WarningRegular />, cls: 'pim-freq-rare' },
  Never: { label: 'nie', icon: <ErrorCircleRegular />, cls: 'pim-freq-never' },
};

const KIND_LABEL: Record<PimAssignmentKind, string> = { Eligible: 'eligible', Active: 'aktiv', Permanent: 'permanent' };
const KIND_COLOR: Record<PimAssignmentKind, 'success' | 'brand' | 'warning'> = {
  Eligible: 'success',
  Active: 'brand',
  Permanent: 'warning',
};
const MEMBERTYPE_LABEL: Record<PimMemberType, string> = { Direct: 'Direkt', Group: 'Gruppe', Inherited: 'Vererbt' };

function SystemPill({ system }: { system: PimSystem }) {
  return (
    <Badge appearance="tint" color={system === 'Entra' ? 'brand' : 'informative'}>
      {system === 'Entra' ? 'Entra' : 'Azure'}
    </Badge>
  );
}

function FreqPill({ frequency }: { frequency: PimFrequency }) {
  const f = FREQ[frequency];
  return (
    <span className={`pim-freq ${f.cls}`} title={f.label}>
      {f.icon}
      {f.label}
    </span>
  );
}

function CountChip({ value, warn, danger }: { value: number; warn: number; danger: number }) {
  const color = value >= danger ? 'danger' : value >= warn ? 'warning' : 'success';
  return (
    <Badge appearance="tint" color={color}>
      {value}
    </Badge>
  );
}

export function PimPage() {
  const [tab, setTab] = useState<PimTab>('roles');

  return (
    <div>
      <PageHeader
        title="PIM"
        description="Privilegierte Rollen (Entra & Azure): Bestand, Aktivierungen, ungenutzte Zuweisungen und Risiken."
      />
      <div className="pim-tabs">
        <TabList selectedValue={tab} onTabSelect={(_, d) => setTab(d.value as PimTab)}>
          <Tab value="roles">Rollen-Übersicht</Tab>
          <Tab value="stale">Stale Assignments</Tab>
        </TabList>
      </div>

      {tab === 'roles' ? <RolesTab /> : <StaleTab />}
    </div>
  );
}

function Kpi({ label, value, tone }: { label: string; value: number; tone?: 'danger' | 'warn' }) {
  return (
    <div className={`pim-kpi${tone ? ` pim-kpi-${tone}` : ''}`}>
      <span className="pim-kpi-num">{value}</span>
      <span className="pim-kpi-label">{label}</span>
    </div>
  );
}

function RolesTab() {
  const api = useApi();
  const [system, setSystem] = useState<PimSystemFilter>('Entra');
  const state = useAsync(() => Promise.all([api.getPimOverview(system), api.getPimSettings()]), [api, system]);

  return (
    <div className="pim-stack">
      <div className="pim-seg" role="radiogroup" aria-label="System">
        {SYSTEMS.map((o) => (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={o.value === system}
            className={o.value === system ? 'pim-seg-item selected' : 'pim-seg-item'}
            onClick={() => setSystem(o.value)}
          >
            {o.label}
          </button>
        ))}
      </div>

      {state.loading ? (
        <Spinner label="Lade PIM-Daten…" />
      ) : state.error ? (
        <MessageBar intent="error">
          <MessageBarBody>{state.error.message}</MessageBarBody>
        </MessageBar>
      ) : state.data ? (
        <RolesContent overview={state.data[0]} settings={state.data[1]} />
      ) : null}
    </div>
  );
}

function RolesContent({ overview: d, settings }: { overview: PimOverviewDto; settings: PimGovernanceSettingsDto }) {
  const s = d.summary;
  const [selected, setSelected] = useState<PimRoleOverviewDto | null>(null);

  return (
    <>
      {!d.dataAvailable ? (
        <MessageBar intent="info">
          <MessageBarBody>
            Keine PIM-Daten verfügbar. Vorausgesetzt sind Entra ID P2 sowie App-Leserechte (RoleManagement.Read.Directory,
            RoleEligibilitySchedule.Read.Directory, PrivilegedAccess.Read.AzureAD, AuditLog.Read.All; für Azure zusätzlich
            „Reader" auf den Subscriptions).
          </MessageBarBody>
        </MessageBar>
      ) : null}

      <div className="pim-kpis">
        <Kpi label="Zuweisungen" value={s.totalAssignments} />
        <Kpi label="Aktiv" value={s.activeCount} />
        <Kpi label="Eligible" value={s.eligibleCount} />
        <Kpi label="Permanent" value={s.permanentCount} tone={s.permanentCount > 0 ? 'warn' : undefined} />
        <Kpi label="Stale" value={s.staleCount} tone={s.staleCount > 0 ? 'warn' : undefined} />
        <Kpi label="Verwaist" value={s.orphanCount} tone={s.orphanCount > 0 ? 'danger' : undefined} />
      </div>
      {s.historySince ? (
        <Text className="pim-muted">Eigener Aktivierungs-Verlauf seit {s.historySince}. Häufigkeit wächst über die Zeit.</Text>
      ) : (
        <Text className="pim-muted">Aktivierungs-Verlauf wird ab jetzt aufgebaut (Audit-Logs reichen ~30 Tage zurück).</Text>
      )}

      {d.hotspots.some((h) => h.warn) ? (
        <MessageBar intent="warning">
          <MessageBarBody>
            Privilegien-Hotspots:{' '}
            {d.hotspots
              .filter((h) => h.warn)
              .map((h) => `${h.roleName} (${h.principalCount})`)
              .join(', ')}{' '}
            — viele Berechtigte für kritische Rollen.
          </MessageBarBody>
        </MessageBar>
      ) : null}

      <div className="pim-roles-layout">
        <div className="pim-roles-main">
          <RolesTable roles={d.roles} settings={settings} selected={selected} onSelect={setSelected} />
        </div>
        {selected ? (
          <RolePanel role={selected} onClose={() => setSelected(null)} />
        ) : null}
      </div>
    </>
  );
}

type SortCol = 'roleName' | 'system' | 'scope' | 'count' | 'activeCount' | 'eligibleCount' | 'permanentCount';

function RolesTable({
  roles,
  settings,
  selected,
  onSelect,
}: {
  roles: PimRoleOverviewDto[];
  settings: PimGovernanceSettingsDto;
  selected: PimRoleOverviewDto | null;
  onSelect: (r: PimRoleOverviewDto) => void;
}) {
  const [sort, setSort] = useState<{ col: SortCol; dir: 'asc' | 'desc' }>({ col: 'permanentCount', dir: 'desc' });

  const sorted = useMemo(() => {
    const sign = sort.dir === 'asc' ? 1 : -1;
    return [...roles].sort((a, b) => {
      const av = a[sort.col];
      const bv = b[sort.col];
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * sign;
      return String(av).localeCompare(String(bv), 'de') * sign;
    });
  }, [roles, sort]);

  function toggle(col: SortCol) {
    setSort((cur) => (cur.col === col ? { col, dir: cur.dir === 'asc' ? 'desc' : 'asc' } : { col, dir: 'asc' }));
  }

  const keyOf = (r: PimRoleOverviewDto) => `${r.system}:${r.scope}:${r.roleDefinitionId}`;
  const selectedKey = selected ? keyOf(selected) : null;

  if (roles.length === 0) {
    return <Text className="pim-muted">Keine PIM-Rollen gefunden.</Text>;
  }

  function Th({ col, label }: { col: SortCol; label: string }) {
    const active = sort.col === col;
    return (
      <TableHeaderCell
        onClick={() => toggle(col)}
        aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
        style={{ cursor: 'pointer', userSelect: 'none' }}
      >
        <span className="pim-th">
          {label}
          {active ? sort.dir === 'asc' ? <ChevronUpRegular /> : <ChevronDownRegular /> : null}
        </span>
      </TableHeaderCell>
    );
  }

  return (
    <div className="pim-table-wrap">
      <Table aria-label="PIM-Rollen" size="small">
        <TableHeader>
          <TableRow>
            <Th col="roleName" label="Rolle" />
            <Th col="system" label="System" />
            <Th col="scope" label="Bereich / Subscription" />
            <Th col="count" label="Count" />
            <Th col="activeCount" label="Aktiv" />
            <Th col="eligibleCount" label="Eligible" />
            <Th col="permanentCount" label="Permanent" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {sorted.map((r) => (
            <TableRow
              key={keyOf(r)}
              onClick={() => onSelect(r)}
              className={selectedKey === keyOf(r) ? 'pim-row selected' : 'pim-row'}
            >
              <TableCell>
                <Text weight="semibold">{r.roleName}</Text>
              </TableCell>
              <TableCell>
                <SystemPill system={r.system} />
              </TableCell>
              <TableCell>
                <span className="pim-scope">{r.scope}</span>
              </TableCell>
              <TableCell>
                <CountChip value={r.count} warn={settings.countWarn} danger={settings.countDanger} />
              </TableCell>
              <TableCell>
                <CountChip value={r.activeCount} warn={settings.activeWarn} danger={settings.activeDanger} />
              </TableCell>
              <TableCell>
                <CountChip value={r.eligibleCount} warn={settings.eligibleWarn} danger={settings.eligibleDanger} />
              </TableCell>
              <TableCell>
                <CountChip value={r.permanentCount} warn={settings.permanentWarn} danger={settings.permanentDanger} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function RolePanel({ role, onClose }: { role: PimRoleOverviewDto; onClose: () => void }) {
  const api = useApi();
  const [members, setMembers] = useState<PimAssignmentDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [removeTargets, setRemoveTargets] = useState<PimAssignmentDto[] | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setMembers(await api.getPimRoleMembers(role.system, role.scope, role.roleDefinitionId));
      setPicked(new Set());
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [api, role]);

  useEffect(() => {
    void load();
  }, [load]);

  function togglePick(id: string) {
    setPicked((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const pickedMembers = members.filter((m) => picked.has(m.id));

  return (
    <div className="pim-role-panel">
      <div className="pim-panel-head">
        <div className="pim-panel-title">
          <Text weight="semibold">{role.roleName}</Text>
          <div className="pim-panel-sub">
            <SystemPill system={role.system} />
            <span className="pim-scope">{role.scope}</span>
          </div>
        </div>
        <Button appearance="subtle" icon={<DismissRegular />} aria-label="Schließen" onClick={onClose} />
      </div>

      {notice ? (
        <MessageBar intent="success" onClick={() => setNotice(null)}>
          <MessageBarBody>{notice}</MessageBarBody>
        </MessageBar>
      ) : null}
      {error ? (
        <MessageBar intent="error">
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      ) : null}

      {loading ? (
        <Spinner size="tiny" label="Lade Mitglieder…" />
      ) : members.length === 0 ? (
        <Text className="pim-muted">Keine Mitglieder.</Text>
      ) : (
        <div className="pim-member-list">
          {members.map((m) => (
            <label className="pim-member" key={m.id}>
              <Checkbox checked={picked.has(m.id)} onChange={() => togglePick(m.id)} />
              <div className="pim-member-main">
                <span className="pii pim-member-name">{m.principalDisplayName}</span>
                {m.principalUpn ? <span className="pim-sub pii">{m.principalUpn}</span> : null}
                <div className="pim-member-tags">
                  <Badge appearance="tint" color={KIND_COLOR[m.assignmentKind]} size="small">
                    {KIND_LABEL[m.assignmentKind]}
                  </Badge>
                  <Badge appearance="outline" size="small">
                    {MEMBERTYPE_LABEL[m.memberType]}
                  </Badge>
                  {!m.principalEnabled ? (
                    <Badge appearance="tint" color="danger" size="small">
                      verwaist
                    </Badge>
                  ) : null}
                </div>
              </div>
            </label>
          ))}
        </div>
      )}

      <div className="pim-panel-foot">
        <Text className="pim-muted">{picked.size} ausgewählt</Text>
        <Button
          appearance="primary"
          icon={<DeleteRegular />}
          disabled={picked.size === 0}
          onClick={() => setRemoveTargets(pickedMembers)}
        >
          Entfernen
        </Button>
      </div>

      <RemoveDialog
        targets={removeTargets}
        onClose={() => setRemoveTargets(null)}
        onDone={(msg) => {
          setRemoveTargets(null);
          setNotice(msg);
          void load();
        }}
      />
    </div>
  );
}

function StaleTab() {
  const api = useApi();
  const [unused, setUnused] = useState(90);
  const [rows, setRows] = useState<PimAssignmentDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [removeTarget, setRemoveTarget] = useState<PimAssignmentDto | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(await api.getPimAssignments(unused));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [api, unused]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="pim-stack">
      <div className="pim-filter">
        <Field label="Nicht aktiviert seit">
          <Dropdown
            style={{ minWidth: '160px' }}
            value={UNUSED_OPTIONS.find((o) => o.value === unused)?.label ?? ''}
            selectedOptions={[String(unused)]}
            onOptionSelect={(_, dt) => dt.optionValue && setUnused(Number(dt.optionValue))}
          >
            {UNUSED_OPTIONS.map((o) => (
              <Option key={o.value} value={String(o.value)} text={o.label}>
                {o.label}
              </Option>
            ))}
          </Dropdown>
        </Field>
      </div>

      {notice ? (
        <MessageBar intent="success" onClick={() => setNotice(null)}>
          <MessageBarBody>{notice}</MessageBarBody>
        </MessageBar>
      ) : null}
      {error ? (
        <MessageBar intent="error">
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      ) : null}

      {loading ? (
        <Spinner label="Lade Zuweisungen…" />
      ) : rows.length === 0 ? (
        <Text className="pim-muted">Keine ungenutzten Zuweisungen im gewählten Zeitraum.</Text>
      ) : (
        <div className="pim-table-wrap">
          <Table aria-label="Stale Assignments" size="small">
            <TableHeader>
              <TableRow>
                <TableHeaderCell>Konto</TableHeaderCell>
                <TableHeaderCell>System</TableHeaderCell>
                <TableHeaderCell>Rolle</TableHeaderCell>
                <TableHeaderCell>Art</TableHeaderCell>
                <TableHeaderCell>Zugang</TableHeaderCell>
                <TableHeaderCell>Letzte Aktivierung</TableHeaderCell>
                <TableHeaderCell>Häufigkeit</TableHeaderCell>
                <TableHeaderCell />
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((a) => (
                <TableRow key={`${a.system}:${a.id}`}>
                  <TableCell>
                    <div className="pim-principal">
                      <Text weight="semibold" className="pii">
                        {a.principalDisplayName}
                      </Text>
                      {a.principalUpn ? <span className="pim-sub pii">{a.principalUpn}</span> : null}
                      {!a.principalEnabled ? (
                        <Badge appearance="tint" color="danger" size="small">
                          verwaist
                        </Badge>
                      ) : null}
                    </div>
                  </TableCell>
                  <TableCell>
                    <SystemPill system={a.system} />
                  </TableCell>
                  <TableCell>{a.roleName}</TableCell>
                  <TableCell>{KIND_LABEL[a.assignmentKind]}</TableCell>
                  <TableCell>{MEMBERTYPE_LABEL[a.memberType]}</TableCell>
                  <TableCell>{a.lastActivationUtc ?? '— nie —'}</TableCell>
                  <TableCell>
                    <FreqPill frequency={a.frequency} />
                  </TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      appearance="subtle"
                      icon={<DeleteRegular />}
                      onClick={() => setRemoveTarget(a)}
                    >
                      Entfernen
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <RemoveDialog
        targets={removeTarget ? [removeTarget] : null}
        onClose={() => setRemoveTarget(null)}
        onDone={(msg) => {
          setRemoveTarget(null);
          setNotice(msg);
          void load();
        }}
      />
    </div>
  );
}

function RemoveDialog({
  targets,
  onClose,
  onDone,
}: {
  targets: PimAssignmentDto[] | null;
  onClose: () => void;
  onDone: (msg: string) => void;
}) {
  const api = useApi();
  const [reason, setReason] = useState('');
  const [ticket, setTicket] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setReason('');
    setTicket('');
    setError(null);
  }, [targets]);

  const open = targets !== null && targets.length > 0;

  async function submit() {
    if (!targets || targets.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      const results = await Promise.all(
        targets.map((t) =>
          api.removePimAssignment({
            assignmentId: t.id,
            system: t.system,
            principalId: t.principalId,
            principalDisplay: t.principalDisplayName,
            roleDefinitionId: t.roleDefinitionId,
            roleName: t.roleName,
            assignmentKind: t.assignmentKind,
            reason: reason.trim() || undefined,
            ticketNumber: ticket.trim() || undefined,
          }),
        ),
      );
      const pending = results.filter((r) => r.status === 'PendingApproval').length;
      const executed = results.length - pending;
      const parts: string[] = [];
      if (executed > 0) parts.push(`${executed} entfernt`);
      if (pending > 0) parts.push(`${pending} zur Freigabe eingereicht (siehe „Meine Anfragen")`);
      onDone(parts.join(' · ') || 'Erledigt.');
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(_, d) => (!d.open ? onClose() : undefined)}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>PIM-Zuweisung entfernen</DialogTitle>
          <DialogContent>
            {targets && targets.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM }}>
                {targets.length === 1 ? (
                  <Text>
                    <span className="pii">{targets[0].principalDisplayName}</span> aus der Rolle{' '}
                    <Text weight="semibold">{targets[0].roleName}</Text> ({targets[0].system},{' '}
                    {KIND_LABEL[targets[0].assignmentKind]}) entfernen?
                  </Text>
                ) : (
                  <Text>
                    <Text weight="semibold">{targets.length}</Text> Zuweisungen entfernen?
                  </Text>
                )}
                <Text className="pim-muted">Je nach Governance ist dies freigabe- und/oder ticketpflichtig.</Text>
                {error ? (
                  <MessageBar intent="error">
                    <MessageBarBody>{error}</MessageBarBody>
                  </MessageBar>
                ) : null}
                <Field label="Begründung">
                  <Textarea value={reason} onChange={(_, d) => setReason(d.value)} placeholder="Warum wird entfernt?" />
                </Field>
                <Field label="Ticketnummer (falls erforderlich)">
                  <Input value={ticket} onChange={(_, d) => setTicket(d.value)} placeholder="z. B. INC-12345" />
                </Field>
              </div>
            ) : null}
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onClose} disabled={busy}>
              Abbrechen
            </Button>
            <Button appearance="primary" onClick={submit} disabled={busy}>
              {busy ? 'Wird verarbeitet…' : 'Entfernen'}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
